import axios, { AxiosRequestConfig, AxiosResponse } from "axios"
import api from "../services/api.service"
import { reportApiError, reportUnexpectedClientError } from '../services/client-error.service'

export interface IBaseResponse {
    status: number
    message: string
    errors: unknown
}

type RequestConfig = AxiosRequestConfig

export const systemRequestConfig = (config: RequestConfig = {}): RequestConfig => ({
    ...config,
    headers: {
        ...(config.headers || {}),
        'X-CRM-System-Request': '1',
    },
})

export const handleApiError = (error: unknown): IBaseResponse => {
    if (!axios.isAxiosError(error)) {
        reportUnexpectedClientError(error, 'api.executeRequest')
        throw error
    }

    if (axios.isCancel(error)) {
        return {
            status: 499,
            message: "Запрос отменён",
            errors: error.code,
        }
    }

    const status = Number(error.response?.status)
    const expectedTransportCodes = new Set(["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"])
    if (!status && !expectedTransportCodes.has(error.code || '')) {
        reportApiError(error)
        throw error
    }

    reportApiError(error)

    if (!status && (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT')) {
        return {
            status: 408,
            message: "Время ожидания ответа истекло. Проверьте результат операции перед повторной отправкой.",
            errors: error.code,
        }
    }

    if (!status && error.code === 'ERR_NETWORK') {
        return {
            status: 500,
            message: "Сервер не доступен",
            errors: error.code,
        }
    }

    return {
        status: error.response?.data?.status ?? status ?? 500,
        message: error.response?.data?.message ?? error.message ?? "Сервер не доступен",
        errors: error.response?.data?.errors ?? null,
    }
}

/** Базовый исполнитель запросов */
const executeRequest = async <T>(
    requestFn: () => Promise<AxiosResponse<T>>
): Promise<T> => {
    try {
        const response = await requestFn()
        return response.data
    } catch (error) {
        return handleApiError(error) as T
    }
}

/** GET */
export const apiGet = <T>(
    url: string,
    config?: RequestConfig
): Promise<T> => executeRequest(() => api.get<T>(url, config))

/** DELETE */
export const apiDelete = <T>(
    url: string,
    config?: RequestConfig,
    data?: unknown
): Promise<T> =>
    executeRequest(() =>
        api.delete<T>(url, {
            ...config,
            data,
        })
    )

/** POST */
export const apiPost = <T>(
    url: string,
    config?: RequestConfig,
    data?: unknown
): Promise<T> => executeRequest(() => api.post<T>(url, data, config))

/** PUT */
export const apiPut = <T>(
    url: string,
    config?: RequestConfig,
    data?: unknown
): Promise<T> => executeRequest(() => api.put<T>(url, data, config))

/** PATCH */
export const apiPatch = <T>(
    url: string,
    config?: RequestConfig,
    data?: unknown
): Promise<T> => executeRequest(() => api.patch<T>(url, data, config))

/** Утилита для FormData */
export const buildFormData = (
    fields: Record<string, string | Blob | File | Array<string | Blob | File> | null | undefined>
): FormData => {
    const formData = new FormData()

    Object.entries(fields).forEach(([key, value]) => {
        if (value == null) return

        if (Array.isArray(value)) {
            const arrayKey = key.endsWith('[]') ? key : `${key}[]`

            value.forEach((item) => {
                formData.append(arrayKey, item)
            })
            return
        }

        formData.append(key, value)
    })

    return formData
}
